/**
 * Admin API service
 *
 * API calls for the admin dashboard, user management, content, AI usage, and audit logs.
 */

import type { UsageQuota } from "@/types/usage";
import { api } from "@/lib/api";

// ── Types ──────────────────────────────────────────────────

/**
 * Shown where cost figures would be when the API does not send them.
 *
 * The frontend and the API deploy separately, and production promotes each by
 * hand, so this bundle can meet an API from before cost tracking. Pages check
 * for the cost fields and say this instead of crashing on them.
 */
export const COST_NOT_REPORTED =
  "This environment's API does not report usage cost yet. It will once the backend is deployed.";

/**
 * What the product cost us, split by who billed us. Every amount is in
 * micro-dollars (1 USD = 1,000,000); format with formatMicroUsd.
 *
 * Provider cost only: Gemini and X. AWS platform cost is not included.
 */
export interface CostSummary {
  totalMicroUsd: number;
  aiMicroUsd: number;
  xMicroUsd: number;
  /** Cost from work outside any user's request, such as scheduled jobs. */
  unattributedMicroUsd: number;
  /** Calls with no published price, recorded at $0, so the total is low. */
  unpricedCalls: number;
  /** Calls priced at the fallback (most expensive) rate, so the total may be high. */
  fallbackCalls: number;
}

export interface TopStreaker {
  userId: string;
  name: string;
  email: string;
  currentWeeklyStreak: number;
  currentDailyStreak: number;
  level: number;
  xp: number;
  badgeCount: number;
}

export interface StreakMetrics {
  usersWithWeeklyStreak: number;
  usersWithDailyStreak: number;
  avgWeeklyStreak: number;
  avgDailyStreak: number;
  topStreakers: TopStreaker[];
}

export interface AdminDashboardMetrics {
  totalUsers: number;
  activeUsers24h: number;
  totalSources: number;
  totalDrafts: number;
  aiGenerationsDailyAvg: number;
  aiGenerationsThisMonth: number;
  /** The billing period the monthly figures cover, `YYYY-MM` in UTC. */
  period: string;
  costThisMonth: CostSummary;
  streakMetrics: StreakMetrics;
}

export interface MetricsDataPoint {
  date: string;
  value: number;
}

export interface MetricsResponse {
  period: string;
  metric: string;
  /** `microUsd` for cost; every other metric is a count. */
  unit: "count" | "microUsd";
  data: MetricsDataPoint[];
  /** Set when the figures are not at the period asked for, and why. */
  note?: string;
}

export interface AdminUserSummary {
  userId: string;
  email: string;
  name?: string;
  plan: string;
  personaCount: number;
  createdAt: string;
  lastLoginAt?: string;
  isActive: boolean;
  totalPublishedPosts: number;
  generationsThisMonth: number;
  costThisMonthMicroUsd: number;
  emailVerified: boolean;
  roles: string[];
}

export interface AdminUserListResponse {
  users: AdminUserSummary[];
  cursor?: string;
  /**
   * The scan was cut short at a page cap before `limit` matching users were
   * found. A cursor is still returned and still works — this only means
   * "fewer than requested came back" does not mean "no more results".
   */
  truncated?: boolean;
}

/**
 * A custom monthly price for one account, set by an admin. It replaces the
 * tier's list price for that account: a discount, or 0 for a trial.
 */
export interface UserBillingOverride {
  priceMicroUsd: number | null;
  note?: string;
  setBy: string;
  setAt: string;
}

/** What an account is charged each month, and where the figure came from. */
export interface EffectivePrice {
  /** Null when the plan has no list price and no custom price is set. */
  priceMicroUsd: number | null;
  source: "list" | "override";
}

/** The fields PATCH /admin/users/:id accepts. */
export interface AdminUserUpdate {
  subscriptionTier?: string;
  isActive?: boolean;
  name?: string;
  /** `priceMicroUsd: null` clears the custom price. */
  billing?: { priceMicroUsd: number | null; note?: string };
}

/** A platform Cognito group — see admin/users/roles.ts for what each grants. */
export type PlatformRoleName =
  | "platform_admin"
  | "platform_support"
  | "platform_analyst";

/** The body POST /admin/users/:id/roles accepts. */
export interface AdminUserRolesUpdate {
  add?: PlatformRoleName[];
  remove?: PlatformRoleName[];
}

/** The response from POST /admin/users/:id/roles. */
export interface AdminUserRolesResponse {
  userId: string;
  roles: PlatformRoleName[];
  /** Present when the role change itself succeeded but a best-effort
   * side effect (signing the account out everywhere) did not. */
  warning?: string;
}

/** One platform account linked to a persona — never a token, see the API's own note. */
export interface AdminPersonaConnection {
  platform: string;
  username: string;
  displayName: string;
  isActive: boolean;
  connectedAt: string;
  lastUsedAt?: string;
}

/** One of a user's personas, as shown on the admin user detail page. */
export interface AdminUserPersona {
  id: string;
  name: string;
  isDefault: boolean;
  isActive: boolean;
  connections: AdminPersonaConnection[];
}

/** One entry in a user's recent-activity timeline — an audit row or a generation. */
export type AdminActivityEntry =
  | {
      type: "audit";
      timestamp: string;
      eventType: string;
      action?: string;
      targetType?: string;
      details?: Record<string, unknown>;
    }
  | {
      type: "generation";
      timestamp: string;
      id: string;
      title: string;
      platform?: string;
      status?: string;
    };

export interface AdminUserDetail {
  userId: string;
  email: string;
  name?: string;
  avatarUrl?: string;
  subscriptionTier: string;
  isActive: boolean;
  emailVerified: boolean;
  roles: string[];
  createdAt: string;
  updatedAt?: string;
  lastLoginAt?: string;
  settings?: Record<string, unknown>;
  onboarding?: Record<string, unknown>;
  usage: {
    generationsThisMonth: number;
    usageResetDate: string;
    /** Since metering started; nothing was recorded before it. */
    totalGenerations: number;
  };
  quota: AdminUserQuota;
  costThisMonth: CostSummary;
  /** The custom price as set; null when the list price applies. Absent from an API older than custom prices. */
  billing?: UserBillingOverride | null;
  /** Absent from an API older than custom prices. */
  price?: EffectivePrice;
  counts: {
    personas: number;
    sources: number;
    drafts: number;
  };
  /** Absent from an API older than this view. */
  personas?: AdminUserPersona[];
  /** Newest first, up to 20. Absent from an API older than this view. */
  recentActivity?: AdminActivityEntry[];
  streaks?: {
    currentWeeklyStreak: number;
    longestWeeklyStreak: number;
    currentDailyStreak: number;
    longestDailyStreak: number;
    totalPublishedPosts: number;
    lastPublishedAt?: string;
    lastDailyPostDate?: string;
    lastWeeklyPostWeek?: string;
    dailyStreakStartDate?: string;
    weeklyStreakStartDate?: string;
    dailyFreezeActive?: boolean;
    weeklyFreezeActive?: boolean;
  } | null;
  gamification?: {
    xp: number;
    level: number;
    badges: string[];
    bonusGenerations: number;
    unlockedFeatures: string[];
  } | null;
  streakFreezes?: {
    weeklyFreezesUsed?: number;
    dailyFreezesUsed?: number;
    lastResetMonth?: string;
  } | null;
}

/** The quota a user sees, plus the X spend behind their post allowance. */
export interface AdminUserQuota extends UsageQuota {
  x: UsageQuota["x"] & {
    /** X spend so far this period, in micro-dollars. */
    usedMicroUsd: number;
    /** The plan's X budget in micro-dollars; `null` means unlimited. */
    budgetMicroUsd: number | null;
  };
}

export interface AdminContentItem {
  id: string;
  userId: string;
  userEmail?: string;
  userName?: string;
  type: string;
  title: string;
  status?: string;
  platform?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface AdminContentListResponse {
  items: AdminContentItem[];
  cursor?: string;
  /** The scan was cut short at a page cap — see AdminUserListResponse.truncated. */
  truncated?: boolean;
}

/** GET /admin/content/{id} — the full item, read-only. */
export interface AdminContentDetail {
  id: string;
  userId: string;
  contentType: "source" | "draft";
  title: string;
  /** Full body text, whitespace as authored. */
  content?: string;
  platform?: string;
  status?: string;
  owner: {
    userId: string;
    email: string;
    name?: string;
  };
  createdAt: string;
  updatedAt?: string;
}

export interface AIUsageTierBreakdown {
  users: number;
  generations: number;
  /** Same generations, in credits (1 credit = $0.001 of AI cost). Absent from an API older than credits. */
  credits?: number;
  costMicroUsd: number;
  /**
   * The month's price across every user on the tier, active or not. Absent
   * from an API older than margin reporting, as are the fields below.
   */
  revenueMicroUsd?: number;
  /** Users with no known price, so not in the revenue figure. */
  unpricedUsers?: number;
  /** The tier's provider cost plus its active users' share of the platform bill. */
  fullyLoadedMicroUsd?: number;
  marginMicroUsd?: number;
}

/**
 * What AWS charged to run the environment this month.
 *
 * A floor, not a total: only resources carrying this app's tags are counted,
 * and the log groups Lambda creates on first invocation are not tagged.
 * `through` is where the figures stop — the current month is always partial,
 * and AWS keeps revising it for days after it ends.
 */
export interface PlatformCost {
  period: string;
  environment: string;
  totalMicroUsd: number;
  byService: Record<string, number>;
  through: string;
  fetchedAt: string;
  coverage: "tagged-resources-only";
}

/**
 * Provider cost plus a share of the platform bill.
 *
 * The provider part is measured. The platform part is an ALLOCATION: AWS bills
 * the environment, and nothing in that bill can be traced to one user, so it is
 * divided evenly between the users who did anything this period.
 */
export interface FullyLoadedCost {
  activeUsers: number;
  platformMicroUsd: number;
  platformPerActiveUserMicroUsd: number;
  perActiveUserMicroUsd: number;
  method: "even-split-across-active-users";
}

/** One published provider price change, projected over this month's volume. */
export interface PriceChangeProjection {
  model: string;
  /** ISO date the new prices take effect, UTC. */
  from: string;
  daysAway: number;
  current: { inputUsdPerMillion: number; outputUsdPerMillion: number };
  next: { inputUsdPerMillion: number; outputUsdPerMillion: number };
  currentMicroUsd: number;
  projectedMicroUsd: number;
  deltaMicroUsd: number;
  /** projected / current - 1; null when there was no volume on the model. */
  deltaRatio: number | null;
}

/** What the bill would be at this month's usage after every pending change lands. */
export interface PriceChangeReport {
  period: string;
  asOf: string;
  changes: PriceChangeProjection[];
  totalMicroUsd: number;
  projectedMicroUsd: number;
  deltaMicroUsd: number;
  deltaRatio: number | null;
  /** Volume on models the rate card does not know; not projected. */
  unpricedModels: string[];
}

export interface AIUsageTopUser {
  userId: string;
  /** Absent for analysts, who do not see who a user is. */
  email?: string;
  name?: string;
  tier: string;
  generationsThisMonth: number;
  /** Same generations, in credits. Absent from an API older than credits. */
  creditsThisMonth?: number;
  costThisMonthMicroUsd: number;
  aiCostMicroUsd: number;
  xCostMicroUsd: number;
  /** Their provider cost plus an even share of the platform bill. */
  fullyLoadedMicroUsd: number;
  /** What they are charged this month; null when nothing says. Absent from an API older than margin reporting. */
  priceMicroUsd?: number | null;
  priceSource?: "list" | "override";
  /** Price less fully-loaded cost; null when the price is unknown. */
  marginMicroUsd?: number | null;
  marginRatio?: number | null;
}

export interface AIUsageResponse {
  /** The billing period the monthly figures cover, `YYYY-MM` in UTC. */
  period: string;
  /** The first metered period; all-time figures start here. */
  meteringStarted: string;
  totalGenerations: number;
  totalGenerationsThisMonth: number;
  /** Same two figures, in credits. Absent from an API older than credits. */
  totalCredits?: number;
  totalCreditsThisMonth?: number;
  totalUsers: number;
  costThisMonth: CostSummary;
  costAllTimeMicroUsd: number;
  /** Null until the daily sync has recorded it; preview stages never do. */
  platformThisMonth: PlatformCost | null;
  fullyLoaded: FullyLoadedCost;
  /** Absent from an API older than the projection. */
  priceChanges?: PriceChangeReport;
  tierBreakdown: Record<string, AIUsageTierBreakdown>;
  /** Ranked by generations this month. */
  topUsers: AIUsageTopUser[];
  /** Ranked by cost this month: not the same list, as X posts cost far more than generations. */
  topCostUsers: AIUsageTopUser[];
  /**
   * Every user's monthly price, list or custom, whether or not they were
   * active. Absent from an API older than margin reporting, as are the
   * fields below.
   */
  revenueMicroUsd?: number;
  /** Users with no known price; counted, not priced. */
  unpricedUsers?: number;
  /** Revenue less the period's whole cost: provider calls plus the platform bill. Null when nothing was charged. */
  marginMicroUsd?: number | null;
  marginRatio?: number | null;
  /** Users whose fully-loaded cost exceeds their price, biggest loss first. Up to 20. */
  belowCost?: AIUsageTopUser[];
}

/** The 7/30/90-day windows GET /admin/ai/usage/daily accepts. */
export type AIUsageDailyWindow = 7 | 30 | 90;

/** One calendar day's generations, credits and cost, platform-wide. */
export interface DailyUsagePoint {
  /** `YYYY-MM-DD`, UTC. */
  date: string;
  generations: number;
  credits: number;
  costMicroUsd: number;
}

export interface AIUsageDailyResponse {
  days: AIUsageDailyWindow;
  /**
   * `YYYY-MM-DD`, UTC — the first day this series is real. Days before it are
   * un-instrumented, not zero-activity: a caller charting `series` must tell
   * the two apart rather than reading every zero the same way.
   */
  since: string;
  series: DailyUsagePoint[];
}

export interface AuditLogEntry {
  timestamp: string;
  eventType: string;
  action?: string;
  adminUserId?: string;
  adminEmail?: string;
  adminName?: string;
  adminRole?: string;
  userId?: string;
  userEmail?: string;
  userName?: string;
  targetType?: string;
  targetId?: string;
  details?: Record<string, unknown>;
  sourceIp?: string;
}

export interface AuditLogResponse {
  date: string;
  entries: AuditLogEntry[];
  cursor?: string;
}

export interface RecalculateStreaksResponse {
  message: string;
  userId: string;
  timezone: string;
  totalPublishedDrafts: number;
  uniqueDaysProcessed: number;
  streaks: Record<string, unknown>;
  gamification: Record<string, unknown>;
}

// ── CloudWatch Logs types ───────────────────────────────────

export interface LogGroup {
  name: string;
  storedBytes: number;
  createdAt: string;
  retentionDays: number | null;
}

export interface LogGroupsResponse {
  groups: LogGroup[];
  total: number;
}

export interface LogEvent {
  timestamp: number;
  message: string;
  logStreamName: string;
  eventId: string;
  ingestionTime: number;
}

export interface LogEventsResponse {
  events: LogEvent[];
  nextToken: string | null;
  logGroup: string;
  startTime: number;
  endTime: number;
}

// ── Feedback types ──────────────────────────────────────────

export interface AdminFeedbackItem {
  feedbackId: string;
  userId: string;
  type: string;
  status: string;
  priority: string;
  title?: string;
  description: string;
  rating?: number;
  pageUrl?: string;
  userAgent?: string;
  appVersion?: string;
  errorMessage?: string;
  stackTrace?: string;
  componentStack?: string;
  attachmentKey?: string;
  adminNotes?: string;
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string;
}

export interface AdminFeedbackListResponse {
  items: AdminFeedbackItem[];
  cursor?: string;
}

// ── API ────────────────────────────────────────────────────

export const adminApi = {
  /** GET /admin/dashboard */
  getDashboard(): Promise<AdminDashboardMetrics> {
    return api.get("/admin/dashboard");
  },

  /** GET /admin/metrics */
  getMetrics(params: {
    period?: string;
    metric?: string;
  }): Promise<MetricsResponse> {
    const qs = new URLSearchParams();
    if (params.period) qs.set("period", params.period);
    if (params.metric) qs.set("metric", params.metric);
    return api.get(`/admin/metrics?${qs.toString()}`);
  },

  /** GET /admin/users */
  listUsers(params?: {
    limit?: number;
    cursor?: string;
    search?: string;
    plan?: string;
    status?: "active" | "disabled";
    createdFrom?: string;
    createdTo?: string;
  }): Promise<AdminUserListResponse> {
    const qs = new URLSearchParams();
    if (params?.limit) qs.set("limit", String(params.limit));
    if (params?.cursor) qs.set("cursor", params.cursor);
    if (params?.search) qs.set("search", params.search);
    if (params?.plan) qs.set("plan", params.plan);
    if (params?.status) qs.set("status", params.status);
    if (params?.createdFrom) qs.set("createdFrom", params.createdFrom);
    if (params?.createdTo) qs.set("createdTo", params.createdTo);
    return api.get(`/admin/users?${qs.toString()}`);
  },

  /** GET /admin/users/:id */
  getUser(userId: string): Promise<AdminUserDetail> {
    return api.get(`/admin/users/${encodeURIComponent(userId)}`);
  },

  /** PATCH /admin/users/:id */
  updateUser(
    userId: string,
    data: AdminUserUpdate
  ): Promise<{ userId: string; updated: Record<string, unknown> }> {
    return api.patch(`/admin/users/${encodeURIComponent(userId)}`, data);
  },

  /** POST /admin/users/:id/disable */
  disableUser(
    userId: string,
    disabled: boolean
  ): Promise<{ userId: string; disabled: boolean }> {
    return api.post(`/admin/users/${encodeURIComponent(userId)}/disable`, {
      disabled,
    });
  },

  /** POST /admin/users/:id/reset-password */
  resetPassword(userId: string): Promise<{ userId: string; message: string }> {
    return api.post(
      `/admin/users/${encodeURIComponent(userId)}/reset-password`
    );
  },

  /** POST /admin/users/:id/roles */
  updateUserRoles(
    userId: string,
    data: AdminUserRolesUpdate
  ): Promise<AdminUserRolesResponse> {
    return api.post(`/admin/users/${encodeURIComponent(userId)}/roles`, data);
  },

  /** GET /admin/content */
  listContent(params?: {
    type?: string;
    limit?: number;
    cursor?: string;
    q?: string;
    createdFrom?: string;
    createdTo?: string;
  }): Promise<AdminContentListResponse> {
    const qs = new URLSearchParams();
    if (params?.type) qs.set("type", params.type);
    if (params?.limit) qs.set("limit", String(params.limit));
    if (params?.cursor) qs.set("cursor", params.cursor);
    if (params?.q) qs.set("q", params.q);
    if (params?.createdFrom) qs.set("createdFrom", params.createdFrom);
    if (params?.createdTo) qs.set("createdTo", params.createdTo);
    return api.get(`/admin/content?${qs.toString()}`);
  },

  /**
   * GET /admin/content/:id
   *
   * `userId` and `type` are required by the API — the id alone does not
   * locate the item in a single-table design keyed by owner. Both come off
   * the list row the caller already has.
   */
  getContent(
    contentId: string,
    params: { userId: string; type?: "source" | "draft" }
  ): Promise<AdminContentDetail> {
    const qs = new URLSearchParams();
    qs.set("userId", params.userId);
    if (params.type) qs.set("type", params.type);
    return api.get(
      `/admin/content/${encodeURIComponent(contentId)}?${qs.toString()}`
    );
  },

  /** GET /admin/ai/usage */
  getAIUsage(): Promise<AIUsageResponse> {
    return api.get("/admin/ai/usage");
  },

  /** GET /admin/ai/usage/daily */
  getAIUsageDaily(days: AIUsageDailyWindow): Promise<AIUsageDailyResponse> {
    return api.get(`/admin/ai/usage/daily?days=${days}`);
  },

  /** GET /admin/audit */
  getAuditLogs(params?: {
    date?: string;
    limit?: number;
    cursor?: string;
    action?: string;
    actorId?: string;
    actorEmail?: string;
  }): Promise<AuditLogResponse> {
    const qs = new URLSearchParams();
    if (params?.date) qs.set("date", params.date);
    if (params?.limit) qs.set("limit", String(params.limit));
    if (params?.cursor) qs.set("cursor", params.cursor);
    if (params?.action) qs.set("action", params.action);
    if (params?.actorId) qs.set("actorId", params.actorId);
    if (params?.actorEmail) qs.set("actorEmail", params.actorEmail);
    return api.get(`/admin/audit?${qs.toString()}`);
  },

  /** POST /admin/users/:id/streaks/recalculate */
  recalculateStreaks(userId: string): Promise<RecalculateStreaksResponse> {
    return api.post(
      `/admin/users/${encodeURIComponent(userId)}/streaks/recalculate`
    );
  },

  /** GET /admin/feedback */
  listFeedback(params?: {
    type?: string;
    status?: string;
    limit?: number;
    cursor?: string;
  }): Promise<AdminFeedbackListResponse> {
    const qs = new URLSearchParams();
    if (params?.type) qs.set("type", params.type);
    if (params?.status) qs.set("status", params.status);
    if (params?.limit) qs.set("limit", String(params.limit));
    if (params?.cursor) qs.set("cursor", params.cursor);
    return api.get(`/admin/feedback?${qs.toString()}`);
  },

  /** GET /admin/feedback/:id */
  getFeedback(feedbackId: string): Promise<AdminFeedbackItem> {
    return api.get(`/admin/feedback/${encodeURIComponent(feedbackId)}`);
  },

  /** PATCH /admin/feedback/:id */
  updateFeedback(
    feedbackId: string,
    data: { status?: string; priority?: string; adminNotes?: string }
  ): Promise<AdminFeedbackItem> {
    return api.patch(`/admin/feedback/${encodeURIComponent(feedbackId)}`, data);
  },

  /** DELETE /admin/feedback/:id */
  deleteFeedback(feedbackId: string): Promise<void> {
    return api.delete(`/admin/feedback/${encodeURIComponent(feedbackId)}`);
  },

  // ── CloudWatch Logs ────────────────────────────────────────

  /** GET /admin/logs/groups */
  getLogGroups(): Promise<LogGroupsResponse> {
    return api.get("/admin/logs/groups");
  },

  /** GET /admin/logs/events */
  getLogEvents(params: {
    logGroup: string;
    startTime?: number;
    endTime?: number;
    filter?: string;
    limit?: number;
    nextToken?: string;
  }): Promise<LogEventsResponse> {
    const qs = new URLSearchParams();
    qs.set("logGroup", params.logGroup);
    if (params.startTime) qs.set("startTime", String(params.startTime));
    if (params.endTime) qs.set("endTime", String(params.endTime));
    if (params.filter) qs.set("filter", params.filter);
    if (params.limit) qs.set("limit", String(params.limit));
    if (params.nextToken) qs.set("nextToken", params.nextToken);
    return api.get(`/admin/logs/events?${qs.toString()}`);
  },
};
